import AddIcon from "@mui/icons-material/Add";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Accordion, AccordionDetails, AccordionSummary, Button, Grid2, Typography } from "@mui/material";
import FormDialog from "components/common/FormDialog";
import { ACTION_STATES } from "core/actions";
import { validMultipleEmailRegex, validPhoneNumberRegex } from "core/validation";
import { Formik } from "formik";
import PropTypes from "prop-types";
import React, { Fragment, useEffect, useState } from "react";
import { connect } from "react-redux";
import * as yup from "yup";
import { actions, initState } from "../logic/contactActions";
import CompanyForm from "./CompanyForm";
import { infoSection } from "./common/styles";
import { companySection } from "./style";

const Form = ({ initialValues, disabled, submitHandlerRef, editMode, handleSubmit }) => {
    const validationScheme = yup.object().shape({
        label: yup.string().required("Required"),
        addressList: yup.array().of(
            yup.object().shape({
                phone1: yup.string().matches(validPhoneNumberRegex, "Invalid phone number.").nullable(),
                phone2: yup.string().matches(validPhoneNumberRegex, "Invalid phone number.").nullable(),
                email: yup.string().matches(validMultipleEmailRegex, "Invalid email.").nullable(),
            })
        ),
    });

    return (
        <Formik
            enableReinitialize
            initialValues={initialValues}
            onSubmit={handleSubmit}
            validationSchema={validationScheme}
        >
            {(props) => <CompanyForm {...props} ref={submitHandlerRef} editMode={editMode} disabled={disabled} />}
        </Formik>
    );
};

Form.propTypes = {
    initialValues: PropTypes.object,
    disabled: PropTypes.bool,
    submitHandlerRef: PropTypes.object,
    editMode: PropTypes.bool,
    handleSubmit: PropTypes.func,
};

const CompanySection = (props) => {
    const companySectionClasses = companySection();
    const infoSectionClasses = infoSection();

    const {
        company,
        saveCompany,
        clearStatus,
        removeCompany,
        savingCompanyStatus,
        openCompanyForm,
        closeForm,
        isCompanyFormOpen,
        companyFormEditMode,
        openContactForm,
    } = props;

    const [expanded, setExpanded] = useState(false);

    let saved = ACTION_STATES.SUCCESS === savingCompanyStatus;

    useEffect(() => {
        if (saved) handleClose();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [savingCompanyStatus]);

    function handleSubmit(values) {
        const newCompany = {
            id: values.id,
            name: values.label,
            addresses: [...values.addressList],
        };
        saveCompany(newCompany);
    }

    function openInEditMode() {
        openCompanyForm(true);
    }

    function handleClose() {
        closeForm();
        clearStatus();
    }

    function handleExpansion() {
        if (!company || !company.id) {
            setExpanded(false);
        }
        setExpanded(!expanded);
    }

    return (
        (<Fragment>
            <FormDialog
                handleClose={handleClose}
                openForm={isCompanyFormOpen}
                formText="Company"
                editMode={companyFormEditMode}
                formComponent={
                    <Form
                        initialValues={companyFormEditMode ? { ...company } : { ...initState.selectedCompany }}
                        handleSubmit={handleSubmit}
                    />
                }
            />
            <Grid2 container spacing={4}>
                <Grid2 className={infoSectionClasses.addButtonContainer} size={12}>
                    <Button
                        variant="outlined"
                        disabled={company !== initState.selectedCompany ? false : true}
                        onClick={() => openContactForm(false)}
                        style={{ marginRight: 30 }}
                    >
                        <AddIcon />
                        Add Contact
                    </Button>
                    <Button variant="outlined" onClick={() => openCompanyForm(false)}>
                        <AddIcon />
                        Add Company
                    </Button>
                </Grid2>
                <Grid2 size={12}>
                    <Accordion expanded={expanded} className={infoSectionClasses.expansionPanel}>
                        <AccordionSummary
                            onClick={handleExpansion}
                            expandIcon={<ExpandMoreIcon />}
                            className={infoSectionClasses.expansionPanelSummay}
                        >
                            <Typography variant={"subtitle1"}>
                                {company && company.id ? company.label : "Select a company"}
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails className={infoSectionClasses.expansionPanelDetails}>
                            <Grid2 container direction="column" className={companySectionClasses.formContainer}>
                                <Form
                                    disabled
                                    initialValues={
                                        company && company.id ? { ...company } : { ...initState.selectedCompany }
                                    }
                                />
                                <Grid2
                                    container
                                    direction="row"
                                    justify="flex-end"
                                    className={infoSectionClasses.buttonsContainer}
                                >
                                    <Button
                                        variant="outlined"
                                        color="secondary"
                                        onClick={openInEditMode}
                                        className={companySectionClasses.editButton}
                                        disabled={!company || !company.id}
                                    >
                                        Edit
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        className={companySectionClasses.deleteButton}
                                        disabled={!company || !company.id}
                                        onClick={() => removeCompany(company.id)}
                                    >
                                        Delete
                                    </Button>
                                </Grid2>
                            </Grid2>
                        </AccordionDetails>
                    </Accordion>
                </Grid2>
            </Grid2>
        </Fragment>)
    );
};

CompanySection.propTypes = {
    company: PropTypes.object,
    saveCompany: PropTypes.func,
    clearStatus: PropTypes.func,
    removeCompany: PropTypes.func,
    savingCompanyStatus: PropTypes.string,
    openCompanyForm: PropTypes.func,
    closeForm: PropTypes.func,
    isCompanyFormOpen: PropTypes.bool,
    companyFormEditMode: PropTypes.bool,
    openContactForm: PropTypes.func,
};

const mapStateToProps = (state) => ({
    company: state.contacts.selectedCompany,
    savingCompanyStatus: state.contacts.savingCompanyStatus,
    isCompanyFormOpen: state.contacts.openCompanyForm,
    companyFormEditMode: state.contacts.companyEditMode,
});

const mapDispatchToProps = (dispatch) => ({
    saveCompany: (company) => dispatch(actions.SAVE_COMPANY.send(company)),
    removeCompany: (id) => dispatch(actions.REMOVE_COMPANY.send(id)),
    clearStatus: () => dispatch(actions.CLEAR_STATUS.call()),
    openCompanyForm: (editMode) => dispatch(actions.OPEN_COMPANY_FORM.call(editMode)),
    openContactForm: (editMode) => dispatch(actions.OPEN_CONTACT_FORM.call(editMode)),
    closeForm: () => dispatch(actions.CLOSE_FORM.call()),
});

export default connect(mapStateToProps, mapDispatchToProps)(CompanySection);
